import once from 'lodash/once';
import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Locale, PulseStatsPlugin } from '@schibsted-svp/web-player';

import type { Plugin, RegisteredPlugin } from '../../types/global';
import { injectStyles } from '../utils';
import { getAssetMetadata, getRawAsset } from '../../utils/player/asset';
import {
  getCategoryMetadataValue,
  getCategoryShortPath,
} from '../../utils/player/category';

import template from './index.html';

import styles from './index.inline.scss';

const LINK_SHOW_AFTER_SECONDS = 5;

function hasEditorialLink(asset: Asset) {
  const { editorialLinkText, editorialLinkUrl } = getAssetMetadata(asset);
  return Boolean(editorialLinkText && editorialLinkUrl);
}

function isAssetSponsored(asset: Asset) {
  const { sponsoredLinkUrl, sponsoredLinkText, sponsored } =
    getAssetMetadata(asset);

  const isSponsored =
    sponsored === 'true' ||
    getCategoryMetadataValue(asset.category, 'sponsored') === 'true';

  const hasSponsoredLink = Boolean(sponsoredLinkText && sponsoredLinkUrl);

  return isSponsored || hasSponsoredLink;
}

function getLocalizedAdLabel(locale?: Locale) {
  return locale === 'se' ? 'ANNONS' : 'ANNONSE';
}

// eslint-disable-next-line consistent-return
function getOverlayData(asset: Asset): LinkData | undefined {
  const { sponsoredLinkUrl, sponsoredLinkText } = getAssetMetadata(asset);
  if (isAssetSponsored(asset) && sponsoredLinkUrl && sponsoredLinkText) {
    return {
      type: 'sponsored',
      text: sponsoredLinkText,
      url: sponsoredLinkUrl,
      sponsor: getCategoryShortPath(asset.category)!,
    };
  }

  if (hasEditorialLink(asset)) {
    const { editorialLinkText, editorialLinkUrl } = getAssetMetadata(asset);
    return {
      type: 'editorial',
      text: editorialLinkText,
      url: editorialLinkUrl,
    };
  }
}

function loadTrackingPixel(pixelUrl: string) {
  if (pixelUrl) {
    const pixel = new Image();
    pixel.src = pixelUrl;
  }
}

function getPixelTrackers(asset: Asset) {
  const {
    pixeltrackerstart,
    pixeltracker25,
    pixeltracker50,
    pixeltracker75,
    pixeltrackerend,
  } = getAssetMetadata(asset);

  return {
    track0: once(() => loadTrackingPixel(pixeltrackerstart)),
    track25: once(() => loadTrackingPixel(pixeltracker25)),
    track50: once(() => loadTrackingPixel(pixeltracker50)),
    track75: once(() => loadTrackingPixel(pixeltracker75)),
    track100: once(() => loadTrackingPixel(pixeltrackerend)),
  };
}

interface SponsorOverlayPluginOptions {}

type LinkData =
  | {
      type: 'sponsored';
      url: string;
      text: string;
      sponsor: string;
    }
  | {
      type: 'editorial';
      url: string;
      text: string;
    };

export interface SponsorOverlayPlugin
  extends Plugin<
    SponsorOverlayPlugin,
    { options: SponsorOverlayPluginOptions }
  > {
  config: SponsorOverlayPluginOptions;
  state: {
    rendered: boolean;
    linkData?: LinkData;
    pixelTrackers?: ReturnType<typeof getPixelTrackers>;
  };

  elements: {
    pluginContainer: HTMLElement | null;
    link: HTMLAnchorElement | null;
    linkLabel: HTMLElement | null;
    linkTitle: HTMLElement | null;
  };

  render(this: RegisteredPlugin<SponsorOverlayPlugin>): void;
  show(this: RegisteredPlugin<SponsorOverlayPlugin>): void;
  setSponsorLink(
    this: RegisteredPlugin<SponsorOverlayPlugin>,
    asset: Asset
  ): void;
  startCounter(this: RegisteredPlugin<SponsorOverlayPlugin>): void;
  hide(this: RegisteredPlugin<SponsorOverlayPlugin>): void;
  updatePixelTrackers(this: RegisteredPlugin<SponsorOverlayPlugin>): void;
  trackProgress(
    this: RegisteredPlugin<SponsorOverlayPlugin>,
    currentTimeInSeconds: number,
    totalTimeInSeconds: number
  ): void;
  trackComplete(this: RegisteredPlugin<SponsorOverlayPlugin>): void;
  getTracker(
    this: RegisteredPlugin<SponsorOverlayPlugin>
  ): PulseStatsPlugin | undefined;
}

const initialState = {
  rendered: false,
  sponsorLink: undefined,
  pixelTrackers: undefined,
};

const initialElements = {
  pluginContainer: null,
  link: null,
  linkLabel: null,
  linkTitle: null,
};

const plugin: SponsorOverlayPlugin = {
  config: {},
  state: { ...initialState },
  elements: { ...initialElements },
  getName() {
    return 'SponsorOverlayPlugin';
  },

  initialize({ options = {} }) {
    this.config = options;

    this.state = { ...initialState };
    this.elements = { ...initialElements };
  },

  setup() {
    this.listenToOnce(this.player, 'ready', this.render.bind(this));

    this.listenTo(this.player, 'assetReady', this.setSponsorLink.bind(this));
    this.listenTo(this.player, 'initialPlay', this.startCounter.bind(this));
    this.listenTo(this.player, 'complete', this.hide.bind(this));

    this.listenTo(
      this.player,
      'assetReady',
      this.updatePixelTrackers.bind(this)
    );
    this.listenTo(this.player, 'time', this.trackProgress.bind(this));
    this.listenTo(this.player, 'complete', this.trackComplete.bind(this));

    this.updatePixelTrackers();

    injectStyles(styles, this.getName());
  },

  render() {
    const playerContainer = this.player.getContainer();
    const asset = getRawAsset(this.player);

    if (!asset || this.state.rendered || !playerContainer) {
      return;
    }

    const pluginContainer = document.createElement('div');

    pluginContainer.className = 'player-plugin-sponsor-overlay';
    pluginContainer.innerHTML = template;

    playerContainer.appendChild(pluginContainer);

    this.elements.pluginContainer = pluginContainer;
    this.elements.link = pluginContainer.querySelector('.sponsor-overlay-link');
    this.elements.linkLabel = pluginContainer.querySelector(
      '.sponsor-overlay-link-label'
    );
    this.elements.linkTitle = pluginContainer.querySelector(
      '.sponsor-overlay-link-title'
    );

    this.setSponsorLink(asset);

    this.elements.link!.addEventListener('click', () => {
      const tracker = this.getTracker();

      if (!this.state.linkData || !tracker) {
        return;
      }

      const provider =
        this.player.getRawAsset()?.provider ||
        this.player.getRawConfig().vendor;

      const brand = provider === 'ab' ? 'aftonbladet' : 'vgtv';

      tracker.trackMediaEvent({
        '@type': 'Engagement',
        'action': 'Click',
        'object': {
          '@id': `sdrn:${brand}:article:${asset.id}:element:${asset.id}`,
          '@type': 'UIElement',
          'spt:custom': {
            'rel': 'noopener',
            'spt:is_sponsored': this.state.linkData.type === 'sponsored',
            ...(this.state.linkData.type === 'sponsored'
              ? { sponsor: this.state.linkData.sponsor }
              : {}),
          },
        },
        'target': {
          url: this.state.linkData.url,
        },
      });
    });

    this.state.rendered = true;
  },

  setSponsorLink() {
    const asset = getRawAsset(this.player);
    const overlayData = asset ? getOverlayData(asset) : undefined;

    if (overlayData) {
      this.state.linkData = overlayData;

      if (this.elements.link) {
        this.elements.link.href = overlayData.url;
        const editorialClassName = 'overlay-editorial';
        if (overlayData.type === 'editorial') {
          this.elements.pluginContainer?.classList.add(editorialClassName);
        } else {
          this.elements.pluginContainer?.classList.remove(editorialClassName);
        }
        if (this.elements.linkLabel) {
          this.elements.linkLabel.innerHTML = overlayData.text;
        }
        if (this.elements.linkTitle) {
          const playerConfig = this.player.getRawConfig();
          this.elements.linkTitle.innerHTML =
            overlayData.type === 'sponsored'
              ? getLocalizedAdLabel(playerConfig.locale)
              : '';
        }
      }
    } else {
      this.state.linkData = undefined;
    }
  },

  startCounter() {
    let secondsPlayed = 0;
    const asset = getRawAsset(this.player);

    if (asset && (isAssetSponsored(asset) || hasEditorialLink(asset))) {
      const onTime = () => {
        secondsPlayed += 0.25; // approx (the time event is fired ~ 4 times per second)

        if (secondsPlayed >= LINK_SHOW_AFTER_SECONDS) {
          this.show();

          this.stopListening(this.player, 'time', onTime);
        }
      };

      this.listenTo(this.player, 'time', onTime);
      this.listenTo(this.player, 'complete', () =>
        this.stopListening(this.player, 'time', onTime)
      );
    }
  },

  updatePixelTrackers() {
    const asset = getRawAsset(this.player);
    if (asset) {
      this.state.pixelTrackers = getPixelTrackers(asset);
    }
  },

  trackProgress(currentTimeInSeconds, totalTimeInSeconds) {
    if (!this.state.pixelTrackers) {
      return;
    }

    const { track0, track25, track50, track75 } = this.state.pixelTrackers;

    track0();

    if (currentTimeInSeconds / totalTimeInSeconds > 0.25) {
      track25();
    }
    if (currentTimeInSeconds / totalTimeInSeconds > 0.5) {
      track50();
    }
    if (currentTimeInSeconds / totalTimeInSeconds > 0.75) {
      track75();
    }
  },

  trackComplete() {
    if (this.state.pixelTrackers) {
      const { track100 } = this.state.pixelTrackers;

      track100();
    }
  },

  show() {
    const { pluginContainer } = this.elements;

    if (this.state.linkData && pluginContainer) {
      pluginContainer.classList.add('visible');
    }
  },

  hide() {
    const { pluginContainer } = this.elements;
    if (pluginContainer) {
      pluginContainer.classList.remove('visible');
    }
  },

  getTracker() {
    return this.player.getPlugin('PulseStats');
  },
};

window.SVP.Player.Plugins.register(plugin);
